/* rein-chat-launcher-2026-09-20 · hookup rewritten 2026-10-03 (CHAT-FIX)
 *
 * Opens the Shopify Inbox / <shopify-chat> widget from a custom card.
 *
 * ⚠️ READ THIS BEFORE DEBUGGING A BREAKAGE.
 * Shopify Inbox exposes NO official JavaScript API. The only way to open it
 * programmatically is to reach into the widget's shadow DOM, find its own
 * activator button and click it. Shopify can change that markup at any time.
 *
 * Inbox markup (verified 2026-10-03 on reinwaterusa.com, unchanged since 09-20):
 *   <shopify-chat view="chat">                     <- child of <body>, shadowRoot OPEN
 *     #shadow-root > #shopify-element-wrapper > .activator-nudge
 *       > button[part="activator"].activator[aria-label="Chat"][aria-expanded]
 *   Open:   host gains `open`; the wrapper then holds
 *           <shopify-agent-runtime exportparts="activator, dialog" open> (own shadow root).
 *   Closed again: host loses `open`; the activator now lives INSIDE the
 *           runtime's shadow root (still reachable as ::part(activator)).
 *
 * CHAT-FIX 2026-10-03 — two defects fixed (root cause in docs/theme.md):
 *   1. The launcher only took over after its reveal trigger (20% scroll / 45 s),
 *      so every visitor first saw Inbox's default "Chat" button. It now takes
 *      over the moment Inbox's activator is found (MutationObserver, <= 15 s).
 *   2. After a chat was opened and closed the launcher never came back while
 *      Inbox's button stayed hidden -> no chat button at all. The host's `open`
 *      attribute is now watched: open -> launcher steps aside, closed -> it returns.
 *
 * FAIL-OPEN: Inbox's own button is hidden (html.rein-chat-armed) ONLY after an
 * activator has been positively found, and a click only counts once the host
 * reports `open`. Any failure removes the class, so customers always keep
 * Inbox's working button, and logs `rein-chat: fallback (<reason>)`.
 */
(function () {
  'use strict';

  var ARMED_CLASS = 'rein-chat-armed';
  var WIDGET_WAIT_MS = 15000;   // how long to wait for Inbox to load
  var OPEN_TIMEOUT_MS = 3000;   // a click must produce `open` within this

  /* Inbox host: the current custom element first, then names it has used or
   * could plausibly use. Script tags (#shopify-chat-*-data) are excluded. */
  var HOST_SELECTORS = [
    'shopify-chat',
    'inbox-online-store-chat',
    '[id^="shopify-chat"]:not(script)',
    '[data-shopify-chat]'
  ];

  /* Activator inside the shadow DOM, most durable first. `part` is a PUBLIC
   * styling contract (::part theming), so Shopify is least likely to rename it.
   * There is deliberately no "any button" fallback: inside the open panel that
   * would hit "Sign in" or "Close chat". */
  var SELECTOR_STRATEGIES = [
    '[part~="activator"]',
    'button.activator',
    'button[aria-label="Chat"]',
    '#shopify-element-wrapper button[aria-expanded]'
  ];

  var root = document.querySelector('[data-rein-chat]');
  if (!root) return;

  var cta = root.querySelector('[data-rein-cta]');
  var minBtn = root.querySelector('[data-rein-min]');
  var bubble = root.querySelector('[data-rein-bubble]');
  var pillStatus = root.querySelector('[data-rein-pill-status]');
  var fallback = root.querySelector('[data-rein-fallback]');
  var statusText = root.querySelector('[data-rein-status-text]');

  var cfg = {
    days: (root.getAttribute('data-hours-days') || '1,2,3,4,5').split(',').map(function (d) { return parseInt(d, 10); }),
    open: root.getAttribute('data-hours-open') || '09:00',
    close: root.getAttribute('data-hours-close') || '17:00',
    tz: root.getAttribute('data-hours-tz') || 'America/New_York',
    openLabel: root.getAttribute('data-label-open') || "We're online — chat live now",
    closedLabel: root.getAttribute('data-label-closed') || "Message us — we'll reply ASAP",
    ariaOpen: root.getAttribute('data-aria-open') || 'Chat with Rein Water USA — online now',
    ariaClosed: root.getAttribute('data-aria-closed') || 'Chat with Rein Water USA — message us'
    /* data-trigger / data-scroll-pct / data-delay are no longer read (CHAT-FIX):
     * gating the bubble on scroll is what left Inbox's default button showing. */
  };

  /* ── business hours, evaluated in the STORE's timezone, never the visitor's ── */
  function storeNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: cfg.tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      var map = {};
      parts.forEach(function (p) { map[p.type] = p.value; });
      var dayIdx = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(map.weekday);
      var hour = parseInt(map.hour, 10);
      if (hour === 24) hour = 0; // some engines emit 24 for midnight
      return { day: dayIdx, minutes: hour * 60 + parseInt(map.minute, 10) };
    } catch (e) {
      return null; // unknown tz or no Intl -> treat as closed, never as "Available"
    }
  }

  function hhmmToMinutes(s) {
    var m = /^(\d{1,2}):(\d{2})$/.exec((s || '').trim());
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  }

  function isOpenNow() {
    var now = storeNow();
    var o = hhmmToMinutes(cfg.open);
    var c = hhmmToMinutes(cfg.close);
    // Any missing/unparseable input resolves to CLOSED.
    if (!now || o === null || c === null) return false;
    if (cfg.days.indexOf(now.day) === -1) return false;
    return now.minutes >= o && now.minutes < c;
  }

  /* Status source = the business-hours schedule above. Shopify Inbox exposes
   * no online/offline state in its DOM or page data (checked 2026-10-03). */
  function paintStatus() {
    var open = isOpenNow();
    var label = open ? cfg.openLabel : cfg.closedLabel;
    root.classList.toggle('rein-chat--open', open);
    if (statusText) statusText.textContent = label;
    if (pillStatus) pillStatus.textContent = label;
    if (bubble) bubble.setAttribute('aria-label', open ? cfg.ariaOpen : cfg.ariaClosed);
  }

  /* ── Inbox detection ── */
  function findHost() {
    for (var i = 0; i < HOST_SELECTORS.length; i++) {
      var el = null;
      try { el = document.querySelector(HOST_SELECTORS[i]); } catch (e) { /* unsupported */ }
      if (el) return el;
    }
    return null;
  }

  /* The host's shadow root plus nested ones (the activator moves into
   * shopify-agent-runtime's root after the first open). Depth-capped. */
  function shadowRoots(host, maxDepth) {
    var roots = [];
    if (!host || !host.shadowRoot) return roots;
    (function collect(r, depth) {
      roots.push(r);
      if (depth >= maxDepth) return;
      var all = r.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        if (all[i].shadowRoot) collect(all[i].shadowRoot, depth + 1);
      }
    })(host.shadowRoot, 0);
    return roots;
  }

  function findActivator(host) {
    var roots = shadowRoots(host, 3);
    for (var i = 0; i < SELECTOR_STRATEGIES.length; i++) {
      for (var r = 0; r < roots.length; r++) {
        var el = null;
        try { el = roots[r].querySelector(SELECTOR_STRATEGIES[i]); } catch (e) { /* unsupported */ }
        if (el && el.tagName === 'BUTTON') return { el: el, strategy: SELECTOR_STRATEGIES[i] };
      }
    }
    return null;
  }

  function isHostOpen(host) {
    return !!(host && host.hasAttribute('open'));
  }

  /* ── state ── */
  var host = null;
  var hostObserver = null;
  var failed = false;
  var returnFocus = false;

  function setArmed(on) {
    document.documentElement.classList.toggle(ARMED_CLASS, on);
  }

  function setCollapsed(v) {
    root.classList.toggle('rein-chat--collapsed', v);
  }

  function showLauncher() {
    paintStatus();
    setCollapsed(true); // never auto-expand; the card opens on a click only
    updateLift();
    root.hidden = false;
  }

  function fallbackTo(reason) {
    // Give Inbox's own button back and stay out of the way for this page view.
    failed = true;
    setArmed(false);
    root.hidden = true;
    hideGreeting();
    if (hostObserver) { hostObserver.disconnect(); hostObserver = null; }
    try { console.warn('rein-chat: fallback (' + reason + ')'); } catch (e) { /* no console */ }
  }

  /* Arm when the host is closed and an activator is reachable; step aside
   * while Inbox's own panel is open. Called on every host mutation. After a
   * close, Inbox re-renders its activator inside a NESTED shadow root (not
   * observable from here), so a miss is retried for a grace period before it
   * counts as "Inbox changed under us". */
  var CLOSE_GRACE_MS = 4000;
  var retryTimer = null;
  var missSince = 0;

  function sync() {
    if (failed || !host) return;
    if (retryTimer) { window.clearTimeout(retryTimer); retryTimer = null; }
    if (isHostOpen(host)) { root.hidden = true; missSince = 0; dismissGreeting(); return; }
    if (findActivator(host)) {
      missSince = 0;
      setArmed(true);
      if (root.hidden) {
        showLauncher();
        // keyboard users who opened chat from the pill land back on it
        if (returnFocus && bubble) { returnFocus = false; try { bubble.focus(); } catch (e) { /* ignore */ } }
      }
      return;
    }
    if (!missSince) missSince = Date.now();
    if (Date.now() - missSince > CLOSE_GRACE_MS) { fallbackTo('activator not found after chat closed'); return; }
    retryTimer = window.setTimeout(sync, 200);
  }

  function watchHost() {
    try {
      hostObserver = new MutationObserver(sync);
      hostObserver.observe(host, { attributes: true, attributeFilter: ['open'] });
      if (host.shadowRoot) hostObserver.observe(host.shadowRoot, { childList: true, subtree: true });
    } catch (e) { /* without MutationObserver the launcher still works; it just won't return after a close */ }
  }

  /* Wait (<= 15 s) for the host AND a usable activator, via MutationObserver on
   * <body> and the host's shadow root, plus a slow poll as a safety net (shadow
   * roots attached after we look are invisible to a body observer). */
  function waitForInbox() {
    var t0 = Date.now();
    var obs = null;
    var poll = null;
    var done = false;

    function finish(ok, reason) {
      if (done) return;
      done = true;
      if (obs) obs.disconnect();
      if (poll) window.clearInterval(poll);
      if (!ok) { fallbackTo(reason); return; }
      watchHost();
      sync();
    }

    function check() {
      if (done) return;
      host = findHost();
      if (host && isHostOpen(host)) { finish(true); return; }          // restored conversation
      if (host && findActivator(host)) { finish(true); return; }
      if (Date.now() - t0 > WIDGET_WAIT_MS) {
        finish(false, host ? (host.shadowRoot ? 'no activator matched in ' + host.tagName.toLowerCase() : 'no readable shadow root on ' + host.tagName.toLowerCase()) : 'Inbox host not found within ' + WIDGET_WAIT_MS / 1000 + 's');
      }
    }

    try {
      obs = new MutationObserver(function () {
        check();
        if (host && host.shadowRoot && !host.__reinObserved) {
          host.__reinObserved = true;
          try { obs.observe(host.shadowRoot, { childList: true, subtree: true, attributes: true }); } catch (e) { /* poll covers it */ }
        }
      });
      obs.observe(document.body, { childList: true });
    } catch (e) { /* poll covers it */ }
    poll = window.setInterval(check, 250);
    check();
  }

  /* ── open ──
   * ⚠️ Once Inbox's panel has been opened once, Inbox closes it on any click
   * OUTSIDE the panel. Our pill's own click is still dispatching when we click
   * the activator, so Inbox opened and then closed again 5 ms later (seen
   * 2026-10-03 on every second open). So: stop our click there, open on the
   * next tick, and only count it as opened once `open` has held for
   * OPEN_STABLE_MS — a flicker is not a success. */
  var OPEN_STABLE_MS = 600;

  function openChat(e) {
    if (e) e.stopPropagation();
    if (failed || !host) return;
    dismissGreeting();
    var hadFocus = document.activeElement === bubble;
    window.setTimeout(function () { doOpen(hadFocus, 1); }, 0);
  }

  function doOpen(hadFocus, attempt) {
    var found = findActivator(host);
    if (!found) { fallbackTo('no activator at click'); revealFallbackLinks(); return; }
    try { found.el.click(); } catch (err) { fallbackTo('click threw: ' + err.message); revealFallbackLinks(); return; }

    var t0 = Date.now();
    var openSince = 0;
    (function confirm() {
      if (isHostOpen(host)) {
        if (!openSince) openSince = Date.now();
        if (Date.now() - openSince >= OPEN_STABLE_MS) { root.hidden = true; returnFocus = hadFocus; return; }
      } else if (openSince) {
        // opened, then closed straight away: try once more, then give up
        if (attempt < 2) { doOpen(hadFocus, attempt + 1); return; }
        fallbackTo('Inbox opened then closed immediately via ' + found.strategy);
        revealFallbackLinks();
        return;
      }
      if (Date.now() - t0 > OPEN_TIMEOUT_MS) {
        fallbackTo('clicked ' + found.strategy + ' but Inbox never opened');
        revealFallbackLinks();
        return;
      }
      window.setTimeout(confirm, 100);
    })();
  }

  function revealFallbackLinks() {
    // fallbackTo() hides the launcher; after a failed CLICK the visitor was
    // mid-action, so keep the card up with the phone number next to Inbox's button.
    if (!fallback) return;
    fallback.hidden = false;
    setCollapsed(false);
    root.hidden = false;
  }

  /* ── lift above anything docked to the bottom of the viewport ──
   * `.sticky-cart` only counts once it carries `is-visible`; the sticky
   * add-to-cart bar (.rein-satc) carries data-rein-bottom-bar. Any new fixed
   * bottom bar must carry that attribute too. */
  var BOTTOM_BAR_SELECTOR = '.sticky-cart.is-visible, [data-rein-bottom-bar]';

  function updateLift() {
    var lift = 0;
    var bars = document.querySelectorAll(BOTTOM_BAR_SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      if (root.contains(el)) continue;
      var s = window.getComputedStyle(el);
      if (s.visibility === 'hidden' || s.display === 'none' || s.opacity === '0') continue;
      var r = el.getBoundingClientRect();
      if (r.height <= 0) continue;
      if (r.bottom < window.innerHeight - 2) continue;
      if (r.top >= window.innerHeight) continue;
      var h = Math.round(window.innerHeight - r.top);
      if (h + 24 > lift) lift = h + 24; // 24px breathing room above the bar (CHAT-BIGGER)
    }
    root.style.setProperty('--rc-lift', lift + 'px');
  }

  function watchBottomBars() {
    updateLift();
    window.addEventListener('scroll', updateLift, { passive: true });
    window.addEventListener('resize', updateLift, { passive: true });
    try {
      new MutationObserver(updateLift).observe(document.body, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style']
      });
    } catch (e) { /* scroll/resize hooks still cover it */ }
  }


  /* ── greeting bubble (CHAT-BIGGER 2026-10-03) ──
   * A white card above the pill, 5 s after load, at most once per browser
   * session. Never on /cart or /checkout, never while the cart drawer, mobile
   * menu or Inbox is open, never unless the launcher is armed (so clicking it
   * always has a working open path). Closing it or opening chat ends it for
   * the session. Mobile: auto-hides after 12 s.
   *
   * CHAT-GREETING-MOBILE 2026-10-04 — mobile (<= 749 px) only:
   *   - shown ONLY on product pages (data-template = Liquid template.name ==
   *     'product'); never home, collections, search, blog, pages or cart —
   *     on the homepage it covered the hero buttons for up to 12 s.
   *   - appears after 40% scroll depth OR 15 s, whichever comes first.
   *   - at display time its box is measured against the add-to-cart button,
   *     the Affirm message and the sticky add-to-cart bar; any overlap -> not
   *     shown on this page view (not marked seen, a later product page may).
   *   - setting "Mobile greeting": product (default) / off.
   * Desktop is unchanged: 5 s, every page except cart. */
  var greet = root.querySelector('[data-rein-greet]');
  var GREET_KEY = 'rein-chat-greeted';
  var GREET_DELAY_MS = 5000;
  var GREET_MOBILE_MS = 12000;
  var greetTimer = null;
  var GREET_MOBILE_SCROLL = 0.4;     // 40% scroll depth
  var GREET_MOBILE_DELAY_MS = 15000; // or 15 s, whichever first
  /* what the mobile greeting must never cover (checked at display time) */
  var GREET_AVOID_SELECTOR = [
    '.product-actions-add-to-cart button[name="add"]',
    'form[action*="/cart/add"] button[name="add"]',
    '.shopify-payment-button',
    '.affirm-as-low-as', '.rein-affirm-pdp',
    '.rein-satc', '[data-rein-bottom-bar]', '.sticky-cart.is-visible'
  ].join(', ');

  function isMobile() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  /* Visible, on-screen boxes the mobile greeting would sit on top of. */
  function greetOverlaps() {
    var g = greet.getBoundingClientRect();
    if (g.width <= 0 || g.height <= 0) return false;
    var els = document.querySelectorAll(GREET_AVOID_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (root.contains(el)) continue;
      var s = window.getComputedStyle(el);
      if (s.visibility === 'hidden' || s.display === 'none' || s.opacity === '0') continue;
      var r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      if (r.left < g.right && r.right > g.left && r.top < g.bottom && r.bottom > g.top) return true;
    }
    return false;
  }

  function greetSeen() {
    try { return window.sessionStorage.getItem(GREET_KEY) === '1'; } catch (e) { return false; }
  }

  function markGreetSeen() {
    try { window.sessionStorage.setItem(GREET_KEY, '1'); } catch (e) { /* private mode etc. */ }
  }

  function greetBlocked() {
    if (/^\/(cart|checkout)(\/|$)/.test(window.location.pathname)) return true;
    var b = document.body.classList;
    if (b.contains('header-mini-cart-open') || b.contains('mobile-menu-open') || b.contains('page-layout-sidebar-drawer-open')) return true;
    if (host && isHostOpen(host)) return true;
    return false;
  }

  function hideGreeting() {
    if (greetTimer) { window.clearTimeout(greetTimer); greetTimer = null; }
    if (greet) { greet.hidden = true; root.classList.remove('rein-chat--greeting'); }
  }

  function dismissGreeting() {
    markGreetSeen();
    hideGreeting();
  }

  function showGreeting() {
    if (!greet || greetSeen() || failed || greetBlocked()) return;
    if (root.hidden || !document.documentElement.classList.contains(ARMED_CLASS)) return;
    var mobile = isMobile();
    if (mobile) {
      // measure in place without painting it, then decide
      greet.style.visibility = 'hidden';
      greet.style.animation = 'none'; // final position, not the slide-in offset
      greet.hidden = false;
      root.classList.add('rein-chat--greeting');
      var covers = greetOverlaps();
      greet.style.visibility = '';
      greet.style.animation = '';
      if (covers) { hideGreeting(); return; }
    }
    markGreetSeen(); // once per session, counted when shown
    greet.hidden = false;
    root.classList.add('rein-chat--greeting');
    if (mobile) greetTimer = window.setTimeout(hideGreeting, GREET_MOBILE_MS);
  }

  function scheduleGreeting() {
    if (!greet || root.getAttribute('data-greeting') === 'false' || greetSeen()) return;
    if (/^\/(cart|checkout)(\/|$)/.test(window.location.pathname)) return;
    var tries = 0;
    function attempt() {
      if (greetSeen() || failed) return;
      // the launcher may still be waiting for Inbox; give it up to 15 s more
      if ((root.hidden || !document.documentElement.classList.contains(ARMED_CLASS)) && tries++ < 15) {
        window.setTimeout(attempt, 1000);
        return;
      }
      showGreeting();
    }
    function start() { window.setTimeout(attempt, GREET_DELAY_MS); }

    if (isMobile()) {
      if (root.getAttribute('data-greeting-mobile') === 'off') return;
      if (root.getAttribute('data-template') !== 'product') return;
      var fired = false;
      var fireTimer = null;
      var fire = function () {
        if (fired) return;
        fired = true;
        window.removeEventListener('scroll', onScroll);
        if (fireTimer) window.clearTimeout(fireTimer);
        attempt();
      };
      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && window.scrollY / max >= GREET_MOBILE_SCROLL) fire();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      fireTimer = window.setTimeout(fire, GREET_MOBILE_DELAY_MS);
      onScroll();
      return;
    }

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  if (greet) {
    var greetOpen = greet.querySelector('[data-rein-greet-open]');
    var greetClose = greet.querySelector('[data-rein-greet-close]');
    if (greetOpen) greetOpen.addEventListener('click', openChat);
    if (greetClose) greetClose.addEventListener('click', function (e) { e.stopPropagation(); dismissGreeting(); });
    // a drawer opening after the bubble is up takes it down for good
    try {
      new MutationObserver(function () { if (!greet.hidden && greetBlocked()) dismissGreeting(); })
        .observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { /* CSS also hides the whole launcher while a drawer is open */ }
  }

  if (cta) cta.addEventListener('click', openChat);
  if (minBtn) minBtn.addEventListener('click', function () { setCollapsed(true); });
  // The pill opens Inbox directly (a native <button>: click, Enter and Space).
  if (bubble) bubble.addEventListener('click', openChat);

  // keep the status honest if a session spans the open/close boundary
  window.setInterval(paintStatus, 60000);

  /* ── ORDERS-ASAP 2026-10-10: never over the title or buy buttons ──
   * Product pages only (data-template == 'product'). While the launcher is
   * collapsed, its pill is measured against the product title, price, Affirm
   * line, Add to cart, Shop Pay / dynamic checkout and the freight-quote
   * button. Left corner first (the gallery side at desktop widths), right
   * corner second; if both would cover one of them, the pill steps aside
   * (rein-chat--tucked) until the visitor scrolls on. On phones the sticky
   * bar's chat button (rein-sticky-atc.js) replaces the pill entirely. */
  var AVOID_SELECTOR = [
    '.rein-pv2__title', '.product-block-price', '.affirm-as-low-as',
    'form[action*="/cart/add"] button[name="add"]', '.shopify-payment-button',
    'shopify-accelerated-checkout', '.rein-fq-trigger'
  ].join(', ');

  function pillCovers() {
    var target = bubble || root;
    var g = target.getBoundingClientRect();
    if (g.width <= 0 || g.height <= 0) return false;
    var els = document.querySelectorAll(AVOID_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (root.contains(el)) continue;
      var r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      if (r.bottom <= 0 || r.top >= window.innerHeight) continue;
      if (r.left < g.right && r.right > g.left && r.top < g.bottom && r.bottom > g.top) return true;
    }
    return false;
  }

  function placePill() {
    if (root.hidden || !root.classList.contains('rein-chat--collapsed')) {
      root.classList.remove('rein-chat--tucked');
      return;
    }
    root.classList.remove('rein-chat--tucked');
    root.classList.add('rein-chat--left');
    if (!pillCovers()) return;
    root.classList.remove('rein-chat--left');
    if (!pillCovers()) return;
    root.classList.add('rein-chat--tucked');
  }

  if (root.getAttribute('data-template') === 'product') {
    var placeQueued = false;
    var queuePlace = function () {
      if (placeQueued) return;
      placeQueued = true;
      window.setTimeout(function () { placeQueued = false; placePill(); }, 60);
    };
    window.addEventListener('scroll', queuePlace, { passive: true });
    window.addEventListener('resize', queuePlace, { passive: true });
    // late layout (images, Affirm, variant pickers) and the launcher arming
    window.setInterval(placePill, 1000);
    placePill();
  }

  // Sticky add-to-cart bar's docked chat button opens through the same path.
  window.reinChatOpen = function (e) { openChat(e); };

  watchBottomBars();
  waitForInbox();
  scheduleGreeting();
})();
